// src/pages/Trend/components/analysis/product-analysis/ColorAnalysisChart.js
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import './ColorAnalysisChart.css';

// BBC Style Configuration
const BBC_STYLE = {
  FONT_FAMILY: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  TITLE_SIZE: '1.5rem',
  SUBTITLE_SIZE: '1rem',
  PRIMARY_COLOR: '#1380A1',  // BBC blue
  SECONDARY_COLOR: '#FAAB18', // BBC yellow/orange
  HIGHLIGHT_COLOR: '#D55E00', // Orange for emphasis
  AXIS_COLOR: '#B0B0B0',
  TEXT_COLOR: '#333333',
  BORDER_COLOR: '#E5E7EB'
};

const COLOR_MAP = {
  "빨강": "#FF6384",
  "파랑": "#36A2EB",
  "초록": "#4BC0C0",
  "노랑": "#FFCE56",
  "보라": "#9966FF",
  "주황": "#FF9F40",
  "분홍": "#FF8FA3",
  "갈색": "#A0522D",
  "회색": "#808080",
  "검정": "#333333",
  "흰색": "#FFFFFF",
  "청록": "#76D7D1",
  "자홍": "#FF9ECD",
  "라임": "#A5E075",
  "틸": "#7BC4C4",
  "네이비": "#7B7BC4",
  "적갈색": "#C48484",
  "올리브": "#B5BA72",
  "은색": "#E8E8E8",
  "금색": "#FFD98B",
  "아이보리": "#ece6cc",
  
  // English color names
  "Red": "#FF6384",
  "Blue": "#36A2EB",
  "Green": "#4BC0C0",
  "Yellow": "#FFCE56",
  "Purple": "#9966FF",
  "Orange": "#FF9F40",
  "Pink": "#FF8FA3",
  "Brown": "#A0522D",
  "Gray": "#808080",
  "Black": "#333333",
  "White": "#FFFFFF",
  "Cyan": "#76D7D1",
  "Magenta": "#FF9ECD",
  "Lime": "#A5E075",
  "Teal": "#7BC4C4",
  "Navy": "#7B7BC4",
  "Maroon": "#C48484",
  "Olive": "#B5BA72",
  "Silver": "#E8E8E8",
  "Gold": "#FFD98B",
  "Ivory": "#ece6cc"
};


const ColorAnalysisChart = ({ primaryData, secondaryData, hasSecondary }) => {
  const { translate } = useLanguage();

  // Hover state for interactivity - MUST be called before any returns
  const [hoveredIndex, setHoveredIndex] = useState(null);

  if (!primaryData?.labels || !primaryData?.values) {
    return (
      <div className="analysis-error">
        {translate('trendPage.productAnalysis.errors.noColorData')}
      </div>
    );
  }

  const formatData = (data) => {
    return data.labels
      .map((label, index) => ({
        name: label,
        value: data.values[index],
        color: COLOR_MAP[label] || '#CCCCCC'
      }))
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value);
  };

  const primaryChartData = formatData(primaryData);
  const secondaryChartData = secondaryData ? formatData(secondaryData) : null;

  const renderList = (data, title, isSecondary = false) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    const maxValue = Math.max(...data.map(item => item.value));

    return (
      <div className="color-list-wrapper">
        {hasSecondary && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginBottom: '0.75rem',
            fontSize: '0.7rem',
            color: '#6B7280',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontWeight: '500',
            paddingBottom: '0.5rem',
            borderBottom: '1px solid #E5E7EB'
          }}>
            <div style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              backgroundColor: isSecondary ? BBC_STYLE.SECONDARY_COLOR : BBC_STYLE.PRIMARY_COLOR
            }} />
            <span>{title}</span>
            <span style={{
              marginLeft: 'auto',
              fontWeight: '700',
              color: BBC_STYLE.TEXT_COLOR,
              fontSize: '0.85rem'
            }}>
              {total}
            </span>
          </div>
        )}

        {/* Color list - ALL colors shown */}
        <div className="color-list">
          {data.map((item, index) => {
            const percentage = ((item.value / total) * 100).toFixed(1);
            const barWidth = (item.value / maxValue) * 100;
            const isHovered = hoveredIndex === index;

            return (
              <div
                key={item.name}
                className="color-list-item"
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                style={{
                  backgroundColor: isHovered ? '#F9FAFB' : 'transparent',
                  transition: 'all 0.2s ease'
                }}
              >
                {/* Color swatch */}
                <div
                  className="color-swatch"
                  style={{
                    backgroundColor: item.color,
                    border: item.color === '#FFFFFF' ? '1px solid #E5E7EB' : 'none'
                  }}
                />

                {/* Color name */}
                <div className="color-name-text">
                  {item.name}
                </div>

                {/* Visual bar */}
                <div className="color-bar-container">
                  <div
                    className="color-bar"
                    style={{
                      width: `${barWidth}%`,
                      backgroundColor: item.color,
                      opacity: isHovered ? 0.9 : 0.7,
                      transition: 'all 0.3s ease'
                    }}
                  />
                </div>

                {/* Count */}
                <div className="color-count-text">
                  {item.value}
                </div>

                {/* Percentage */}
                <div className="color-percentage-text">
                  {percentage}%
                </div>
              </div>
            );
          })}
        </div>

        {/* Total footer - only show when single filter */}
        {!hasSecondary && (
          <div style={{
            marginTop: '0.75rem',
            paddingTop: '0.75rem',
            borderTop: '1px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{
              fontSize: '0.7rem',
              color: '#9CA3AF',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              Total
            </span>
            <span style={{
              fontSize: '1rem',
              fontWeight: '700',
              color: BBC_STYLE.TEXT_COLOR
            }}>
              {total}
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bbc-color-analysis-container">
      <div className={`color-lists-container ${hasSecondary ? 'has-two' : 'has-one'}`}>
        {renderList(primaryChartData, translate('trendPage.filters.set1'), false)}
        {hasSecondary && secondaryChartData &&
          renderList(secondaryChartData, translate('trendPage.filters.set2'), true)
        }
      </div>
    </div>
  );
};

ColorAnalysisChart.propTypes = {
  primaryData: PropTypes.shape({
    labels: PropTypes.arrayOf(PropTypes.string),
    values: PropTypes.arrayOf(PropTypes.number)
  }).isRequired,
  secondaryData: PropTypes.shape({
    labels: PropTypes.arrayOf(PropTypes.string),
    values: PropTypes.arrayOf(PropTypes.number)
  }),
  hasSecondary: PropTypes.bool.isRequired
};

export default ColorAnalysisChart;
